import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Activity, Dumbbell, Apple, Camera, LifeBuoy, Play, Pause, Square, SkipForward, CheckCircle, Flag } from 'lucide-react';
import { useWorkoutControls } from '../../context/WorkoutControlsContext';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '../ui/drawer';

/**
 * QuickActionsFab — botón de acción flotante (solo móvil) con hoja inferior
 * de accesos rápidos. Se apoya en vaul (Drawer). Themeable: usa el color de
 * acento (--primary) y respeta el safe-area del home indicator.
 */
const ACTIONS = [
  { label: 'Biometría', desc: 'Registrar medidas', icon: Activity, to: '/app/biometrics' },
  { label: 'Entrenar', desc: 'Ir al Hub', icon: Dumbbell, to: '/app/hub' },
  { label: 'Nutrición', desc: 'Plan y comidas', icon: Apple, to: '/app/nutrition' },
  { label: 'Foto', desc: 'Progreso visual', icon: Camera, to: '/app/progress/photos' },
  { label: 'Reportar', desc: 'Dolor, duda o queja', icon: LifeBuoy, to: '/app/reports' },
];

const CONTROL_ICON = { play: Play, pause: Pause, stop: Square, skip: SkipForward, log: CheckCircle, done: Flag };

const QuickActionsFab = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  // Durante la sesión de entrenamiento el disco se convierte en el botón de
  // acción principal (iniciar/detener cronómetro, registrar serie, saltar descanso).
  const { control } = useWorkoutControls();
  const ControlIcon = control ? (CONTROL_ICON[control.mode] || Play) : null;
  // La pantalla de resultados de la sesión pide ocultar el disco: su barra de
  // salida ocupa ese lugar.
  if (control?.hidden) return null;

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      {/* FAB — flota sobre la barra inferior, respetando el safe-area. */}
      <button
        type="button"
        onClick={() => (control ? control.onPress() : setOpen(true))}
        aria-label={control ? control.label : 'Acciones rápidas'}
        title={control ? control.label : undefined}
        data-testid="quick-actions-fab"
        className="lg:hidden fixed left-1/2 -translate-x-1/2 z-[60] w-16 h-16 min-h-[44px] min-w-[44px]
                   rounded-full flex flex-col items-center justify-center
                   transition-transform duration-normal ease-spring
                   hover:scale-105 active:scale-95 active:translate-y-px"
        style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom))', filter: 'drop-shadow(0 8px 20px rgba(0,0,0,.6))' }}
      >
        <img
          src="/brand/disc.webp"
          alt=""
          width="64"
          height="64"
          draggable="false"
          className={`w-16 h-16 transition-transform duration-slow ease-spring ${open ? 'rotate-45' : ''} ${control?.active ? 'onor-disc-spin' : ''}`}
        />
        <Plus className="hidden" />
        {control && (
          <>
            {control.active && <span className="absolute inset-0 rounded-full border-2 border-primary/70 animate-ping" aria-hidden="true" />}
            <span
              className="absolute inset-0 m-auto w-9 h-9 rounded-full flex items-center justify-center border border-primary bg-zinc-950/90 text-primary"
              style={{ boxShadow: '0 2px 10px rgba(0,0,0,.6)' }}
              aria-hidden="true"
            >
              <ControlIcon className="w-4 h-4" />
            </span>
            <span className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-display uppercase tracking-wider text-primary bg-zinc-950/90 px-2 py-0.5 rounded-sm border border-zinc-800">
              {control.label}
            </span>
          </>
        )}
      </button>

      <DrawerContent className="border-zinc-800 pb-safe">
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-display font-bold text-xl text-white uppercase">
            Acciones rápidas
          </DrawerTitle>
          <DrawerDescription className="text-zinc-500">
            Salta directo a lo que quieres hacer ahora.
          </DrawerDescription>
        </DrawerHeader>

        <div className="grid grid-cols-2 gap-3 p-4 pt-0">
          {ACTIONS.map((a) => {
            const Icon = a.icon;
            return (
              <button
                key={a.to}
                onClick={() => go(a.to)}
                data-testid={`fab-action-${a.label.toLowerCase()}`}
                className="group flex flex-col items-start gap-2 p-4 rounded-sm border border-zinc-800
                           bg-zinc-900/60 text-left min-h-[44px]
                           transition-all duration-normal ease-out-atl
                           hover:border-primary/50 hover:-translate-y-0.5 active:scale-[0.98]"
              >
                <span className="w-10 h-10 rounded-full flex items-center justify-center bg-primary/10 text-primary transition-colors duration-normal group-hover:bg-primary/20">
                  <Icon className="w-5 h-5" />
                </span>
                <span className="font-display font-bold text-white text-sm uppercase tracking-wide">
                  {a.label}
                </span>
                <span className="text-zinc-500 text-xs">{a.desc}</span>
              </button>
            );
          })}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default QuickActionsFab;
